// The completions half of the staff "Prequalification" tab, on a Broker /
// Brokerage record. Extracted from the tab itself, where it was a local block
// that rendered whatever fixed window the site card happened to carry.
//
// A PANEL, not a section: the tab owns the heading (the sub-tab label says
// "Completions") and mounts exactly one panel at a time, so this file renders a
// list and its controls and nothing above them.
//
// The site is resolved SERVER-SIDE from the record id — this panel never names
// a brand, and on an INHERITED broker site the rows are the covering FIRM's.
// There is no per-broker attribution on a submission, so the caller passes the
// caption that says whose these are; read without it, an inherited list is
// simply wrong.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, SearchField, SortSelect } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { formatCurrency } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  useBrokerPrequalSiteCompletions,
  useBrokeragePrequalSiteCompletions,
  useBuyerTeamPrequalSiteCompletions,
} from "@/hooks/partners/usePrequalSiteAdmin";
import type { PrequalSiteCompletionRow, PrequalSort } from "@/api/prequalSiteAdmin";

/** Matches the server's own default (`PrequalSiteCompletionListQuery.PageSize`)
 *  AND the window the site card already carries, which is what makes the seed
 *  below an identity rather than an approximation. */
const PAGE_SIZE = 25;

/** Keystrokes are cheap; a paged server query over an unbounded row count is
 *  not. Matches the portal's established search debounce. */
const SEARCH_DEBOUNCE_MS = 300;

/** Display labels only. The two WIRE tokens are single-sourced as `PrequalSort`
 *  (server: `PrequalListSort`); this array just names them, and the three panels
 *  on this tab deliberately show the same two so one sort control means one
 *  thing everywhere. */
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

export interface PrequalSiteCompletionsPanelProps {
  ownerKind: "broker" | "brokerage" | "buyerTeam";
  /** The record this tab is mounted on. The server resolves the site from it —
   *  a record-scoped read never takes a caller-supplied brand. */
  ownerId: string;
  /**
   * Page 1, newest-first, unsearched — the exact window the site card's own
   * read already returned. See `isSeedQuery`.
   */
  seedRows: PrequalSiteCompletionRow[];
  seedTotal: number;
  /**
   * REQUIRED and nullable rather than optional, so mounting this without
   * deciding is a compile error rather than a silent omission. Non-null
   * whenever these rows belong to a wider population than the record itself —
   * an inherited broker site, or a firm site every member inherits.
   */
  caption: string | null;
}

export function PrequalSiteCompletionsPanel({
  ownerKind,
  ownerId,
  seedRows,
  seedTotal,
  caption,
}: PrequalSiteCompletionsPanelProps) {
  const { date } = useDateFormatter();
  const { hasPermission } = useAuth();
  // The drill-in route is gated Prequalification.Read; render plain text for a
  // viewer who would only land on a permission wall.
  const canDrillIn = hasPermission("Prequalification.Read");

  // `rawSearch` is what the box shows; `q` is what the server has been asked.
  // Two states rather than one so typing stays instant while the query behind
  // it moves at most once per pause.
  const [rawSearch, setRawSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    // Nothing to debounce — including on mount, where this effect otherwise
    // scheduled an unconditional setPage(1) that landed ~300ms in and silently
    // reverted a Next pressed inside that window.
    const trimmed = rawSearch.trim();
    if (trimmed === q) return;
    const timer = window.setTimeout(() => {
      setQ(trimmed);
      // A new search is a new result set; staying on page 4 of the old one
      // would show an empty page and read as "no matches".
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rawSearch, q]);

  // 🔴 The seeding rule. The tab's mount fetch IS the site resolver — it cannot
  // be removed — and it answers exactly this query: page 1, size 25, no search,
  // newest first. So while every control is still sitting on its default,
  // `seedRows` is not a cached approximation of what the server would say, it is
  // what the server already said, and fetching again would ask the identical
  // question twice on the panel that opens first.
  //
  // Stated as a pure function of the controls rather than a used-once latch, so
  // it is the same answer every render and a staff member who pages forward and
  // comes straight back does not pay for a round trip to see the list they were
  // just looking at.
  const isSeedQuery = page === 1 && q === "" && sort === "newest";

  const query = { page, pageSize: PAGE_SIZE, q: q || undefined, sort };
  // Both hooks are called unconditionally (rules of hooks) and each is disabled
  // unless it is the one this record needs AND the controls have moved off the
  // seed. A disabled query issues no request.
  const brokerQuery = useBrokerPrequalSiteCompletions(
    ownerKind === "broker" ? ownerId : "",
    query,
    ownerKind === "broker" && !isSeedQuery,
  );
  const brokerageQuery = useBrokeragePrequalSiteCompletions(
    ownerKind === "brokerage" ? ownerId : "",
    query,
    ownerKind === "brokerage" && !isSeedQuery,
  );
  const buyerTeamQuery = useBuyerTeamPrequalSiteCompletions(
    ownerKind === "buyerTeam" ? ownerId : "",
    query,
    ownerKind === "buyerTeam" && !isSeedQuery,
  );
  const fetched =
    ownerKind === "broker"
      ? brokerQuery
      : ownerKind === "brokerage"
        ? brokerageQuery
        : buyerTeamQuery;

  const rows = isSeedQuery ? seedRows : (fetched.data?.data ?? []);
  const totalCount = isSeedQuery ? seedTotal : (fetched.data?.totalCount ?? 0);
  const pageSize = isSeedQuery ? PAGE_SIZE : (fetched.data?.pageSize ?? PAGE_SIZE);
  const currentPage = isSeedQuery ? 1 : (fetched.data?.page ?? page);
  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
  const searching = q !== "";

  const columns: Column<PrequalSiteCompletionRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (r) => {
        const name = `${r.firstName} ${r.lastName}`.trim() || "—";
        return (
          <div>
            {canDrillIn ? (
              <Link
                to={`/deal-flow/prequalification/${r.id}`}
                className="font-semibold text-gold-shadow hover:underline"
              >
                {name}
              </Link>
            ) : (
              <span className="font-semibold">{name}</span>
            )}
            {/* Identity without a contact address is not actionable in the
                support conversation this tab exists for. Mirrors the Applicant
                column on the cross-book drafts queue. */}
            <div className="text-caption text-gray-mid">{r.email}</div>
          </div>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      render: (r) => <Badge status={r.role === "Buyer" ? "info" : "gold"}>{r.role}</Badge>,
    },
    { key: "amount", header: "Amount", render: (r) => figure(r) },
    {
      key: "outcome",
      header: "Outcome",
      render: (r) => (
        <Badge status={r.outcome === "Pass" ? "gold" : "review"}>
          {r.outcome === "Pass" ? "Pass" : "Soft decline"}
        </Badge>
      ),
    },
    {
      key: "documents",
      header: "Documents",
      render: (r) => (
        <div className="flex flex-wrap gap-space-1">
          {r.hasLetter && <Badge status="draft">Letter</Badge>}
          {r.hasDealSheet && <Badge status="draft">Deal sheet</Badge>}
          {/* 🔴 Says WHY there is no deal sheet, rather than leaving a reader to
              guess. A letter-only buyer asked for exactly what they got, and a
              column that shows only what exists cannot tell that apart from a
              document that failed to generate. Muted prose, not a pill: it is
              the absence of a document, not a state of one.

              Verbatim the line the cross-book staff list renders. Two surfaces
              answer the same question about the same submission, so they say it
              in the same words — a paraphrase here would read as a different
              fact. */}
          {r.buyerDocumentPath === "LetterOnly" && (
            <span className="text-caption text-gray-mid">Letter only, by request</span>
          )}
          {/* 🔴 EXCLUSIVE OF THE ROW ABOVE. Left independent, a letter-only
              buyer whose LETTER failed renders both in one flex row —
              "Letter only, by request —" — which reads as "the absence is by
              request" and is exactly the misreading the note above exists to
              prevent. That row IS an incident and the server-side missing-
              documents filter flags it as one.

              Keyed on LetterOnly rather than on "a package was recorded": a
              LetterAndDealSheet buyer holding nothing gets no explanatory note,
              so the dash is the only thing that would say anything at all. */}
          {r.buyerDocumentPath !== "LetterOnly"
            && !r.hasLetter && !r.hasDealSheet && (
            <span className="text-caption text-gray-mid">—</span>
          )}
        </div>
      ),
    },
    { key: "status", header: "Status", render: (r) => r.status },
    { key: "submitted", header: "Submitted", render: (r) => date(r.createdAt) },
  ];

  return (
    <section data-testid="prequal-site-completions" aria-label="Completions">
      {caption && (
        <p
          className="mb-space-2 mt-0 text-caption text-gray-mid"
          data-testid="prequal-site-completions-caption"
        >
          {caption}
        </p>
      )}

      <div className="mb-space-3 flex flex-wrap items-center gap-space-2">
        {/* 🔴 No `label` prop. It defaults to "Search", and that default is
            load-bearing: a label matching /email/i would make the new
            prequalification wizard's own Email field ambiguous to a by-label
            query. */}
        <SearchField
          className="min-w-[220px] flex-1"
          value={rawSearch}
          onChange={setRawSearch}
          placeholder="Search by name or email…"
        />
        <SortSelect
          value={sort}
          onChange={(next) => {
            setSort(next as PrequalSort);
            setPage(1);
          }}
          options={SORT_OPTIONS}
        />
      </div>

      {totalCount > 0 && (
        <p className="mb-space-2 mt-0 text-caption text-gray-mid" data-testid="prequal-site-completions-total">
          {totalCount === 1 ? "1 completion" : `${totalCount} completions`}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={!isSeedQuery && fetched.isLoading}
        error={!isSeedQuery && fetched.isError ? (fetched.error as Error).message : null}
        emptyMessage={
          searching
            ? // Names the boundary rather than leaving staff to guess it. A
              // reference number is the most obvious thing to paste in here and
              // the one thing search deliberately does not look at.
              "No completions match your search. Search looks at the applicant's name and email address — reference numbers aren't searchable."
            : "Nobody has completed a prequalification through this site yet."
        }
        rowKey={(r) => r.id}
      />

      {totalCount > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}

function figure(r: PrequalSiteCompletionRow) {
  if (r.role === "Buyer") {
    return r.qualifiedAmount != null ? formatCurrency(r.qualifiedAmount) : "—";
  }
  if (r.supportablePriceLow != null && r.supportablePriceHigh != null) {
    return `${formatCurrency(r.supportablePriceLow)} – ${formatCurrency(r.supportablePriceHigh)}`;
  }
  return "—";
}
